<!--@format-->
<template>
    <div class="inline-solution-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>Edit Line Item</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <div class="form-wrapper">
                    <div class="solution-widget-container">
                        <span class="solution-widget-header">Details</span>
                        <div class="widget-input-container">
                            <app-input
                                v-model="solution.name"
                                label="Name"
                                placeholder="Item name"
                                :is-valid="!submitted || $v.solution.name.required"
                                error="Name is required"
                            />
                        </div>
                        <div class="widget-input-container">
                            <label>Description</label>
                            <app-textarea
                                v-model="solution.description"
                                placeholder="Start typing to add a description of this item..."
                            />
                        </div>
                        <div class="widget-input-container">
                            <label>Product Image</label>
                            <div v-if="solution.previewImageUrl" class="preview-image-solutions">
                                <div class="trash-can" @click="deleteFile">
                                    <trash-can-svg />
                                </div>
                                <img
                                    :src="solution.previewImageUrl"
                                    :alt="solution.previewImageUrlName"
                                />
                            </div>
                            <loading-screen-svg v-else-if="isImageLoading" />
                            <app-drag-and-drop-area
                                v-else
                                label="Choose an image source or drag image here "
                                type="image"
                                :file-accept="fileConfig.fileSelectorAllowedTypes"
                                icon-library
                                @upload="handleUpload"
                                @remove="deleteFile"
                                @icon-library-opened="setContext('product')"
                                @library-opened="setContext('product')"
                                @library:selected="handleCollateralSelected"
                                @icon:selected="handleIconSelected"
                            />
                        </div>
                        <div class="widget-input-container">
                            <label>Video Description (optional)</label>
                            <div v-if="solution.videoUrl" class="preview-video-solutions">
                                <div class="trash-can" @click="handleDelete">
                                    <trash-can-svg></trash-can-svg>
                                </div>
                                <div v-if="isValidYoutubeUrl(solution.videoUrl)" class="ratio-box">
                                    <iframe
                                        id="`yt-player`"
                                        :src="youtubeEmbedUrl"
                                        title="YouTube video player"
                                        frameborder="0"
                                        width="256"
                                        height="144"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowfullscreen
                                        referrerpolicy="strict-origin-when-cross-origin"
                                    >
                                    </iframe>
                                </div>
                                <video v-else controls="controls" :poster="solution.videoThumbnail">
                                    <source :src="solution.videoUrl" type="video/mp4" />
                                    "Your browser does not support the video tag"
                                </video>
                            </div>
                            <loading-screen-svg v-else-if="isVideoLoading" />
                            <app-drag-and-drop-area
                                v-else
                                type="video"
                                label="Or provide a YouTube URL"
                                :file-accept="videoFileConfig.fileSelectorAllowedTypes"
                                @upload="handleVideoUpload"
                                @library-opened="setContext('video')"
                                @library:selected="handleLibrarySelect"
                            >
                                <template slot="URLInput">
                                    <app-input
                                        class="video-input"
                                        :value="solution.videoUrl"
                                        placeholder="www.yourvideo.com"
                                        :is-valid="!hasError"
                                        error="Please enter a valid Youtube link"
                                        @input="handleInput"
                                    />
                                </template>
                            </app-drag-and-drop-area>
                        </div>
                    </div>
                    <div class="solution-widget-container">
                        <span class="solution-widget-header">Pricing</span>
                        <div class="widget-input-container">
                            <app-input
                                :value="solution.price"
                                label="Price"
                                min="0"
                                placeholder="0.00"
                                type="number"
                                :is-valid="!submitted || $v.solution.price.minValue"
                                error="Unit Price is required"
                                @input="updateValue('price', +$event)"
                            />
                        </div>
                        <div class="widget-input-container">
                            <label>Billing cycle</label>
                            <div class="billing-type-unit-wrapper">
                                <solution-billing-cycle-dropdown v-model="solution.billingCycle" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="cancel-btn transparent-btn" @click="closeWidget">
                        Cancel
                    </app-button>
                </div>
                <div class="action-container">
                    <app-button :disabled="isImageLoading || isVideoLoading" @click="save">
                        Update Item
                    </app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg.vue');
const AppButton = () => import('@/components/common/app-button.vue');
const AppDragAndDropArea = () => import('@/components/common/app-drag-and-drop-area.vue');
const AppInput = () => import('@/components/common/app-input.vue');
const AppTextArea = () => import('@/components/common/app-textarea.vue');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const SolutionBillingCycleDropdown = () =>
    import('@/components/solutions/solution-billing-cycle-dropdown.vue');
const TrashCanSvg = () => import('@/components/svg/trash-can-svg');

import { FileService } from '@/scripts/services/file/file-service';
import FileMixin, { FileByteSizeLimitError, FileTypeError } from '@/mixins/file-mixin';
import EventBus from '@/scripts/event-bus.js';
import _ from 'lodash';

import { required } from 'vuelidate/lib/validators';

const minValue = (value) => {
    return value !== null && value >= 0;
};

export default {
    name: 'inline-solution-widget',

    components: {
        'app-button': AppButton,
        'app-drag-and-drop-area': AppDragAndDropArea,
        'app-input': AppInput,
        'app-textarea': AppTextArea,
        'loading-screen-svg': LoadingScreenSvg,
        'solution-billing-cycle-dropdown': SolutionBillingCycleDropdown,
        'widget-close-svg': WidgetCloseSvg,
        'trash-can-svg': TrashCanSvg,
    },

    mixins: [FileMixin],

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            context: null,
            hasError: false,
            videoSrcName: null,
            submitted: false,
            isImageLoading: false,
            isVideoLoading: false,
            solution: null,
            fileConfig: {
                byteSizeLimit: 40000000,
                fileSelectorAllowedTypes: ['jpeg', 'jpg', 'png', 'webp'],
                allowedImageTypes: ['image/png', 'image/jpg', 'image/jpeg', 'image/webp'],
            },

            videoFileConfig: {
                byteSizeLimit: 20971520,
                fileSelectorAllowedTypes: ['mp4', 'webm'],
                allowedFileTypes: ['video/mp4', 'video/webm'],
            },
        };
    },

    validations: {
        solution: {
            name: { required },
            billingCycle: { required },
            price: { minValue },
        },
    },

    computed: {
        youtubeEmbedUrl() {
            if (this.solution.videoUrl) {
                const videoId = this.solution.videoUrl.match(/v=([^&]+)/);
                return videoId
                    ? `https://www.youtube.com/embed/${videoId[1]}?showinfo=0&controls=0&autohide=1`
                    : null;
            } else {
                return null;
            }
        },
    },

    created() {
        this.setSolutionData();
    },

    methods: {
        setSolutionData() {
            this.solution = {
                ...this.widget.solution,
                billingCycle: this.$helpers
                    .getSolutionBillingCycles()
                    .find((el) => el.name === this.widget.solution.billingCycle),
            };
        },

        save() {
            this.submitted = true;
            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }

            const solutionCopy = {
                ..._.cloneDeep(this.solution),
                billingCycle: this.solution.billingCycle.name,
            };

            if (this.widget.eventName) {
                EventBus.$emit(this.widget.eventName, solutionCopy);
            } else {
                EventBus.$emit('solution:edit-inline-save', solutionCopy);
            }

            this.closeWidget();
        },

        updateValue(property, value) {
            this.solution[property] = value;
            this.$v.solution[property].$touch();
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        deleteFile() {
            this.solution.previewImageUrl = null;
            this.solution.isPreviewImageSvg = false;
        },

        isValidYoutubeUrl(url) {
            const youtubeUrlPattern =
                /^(https?:\/\/(www\.)?(youtube\.com|youtu\.be)\/(watch\?v=[\w-]+.*|channel\/[\w-]+.*|playlist\?list=[\w-]+.*))$/;
            return youtubeUrlPattern.test(url);
        },

        handleInput(value) {
            if (value === '') {
                this.handleRemove();
            } else {
                if (this.isValidYoutubeUrl(value)) {
                    const params = new URLSearchParams(new URL(value).search);
                    const videoId = params.get('v');
                    const thumbnail = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
                    this.solution.videoThumbnail = thumbnail;
                    this.solution.videoUrl = value;
                    this.hasError = false;
                } else {
                    this.hasError = true;
                }
            }
        },

        handleUpload(e) {
            this.isImageLoading = true;
            this.getBase64Url(e, this.fileConfig)
                .then(({ encodingStr, name }) => {
                    return this.mediaRequest({ name, url: encodingStr });
                })
                .then(({ fileName, url }) => {
                    this.solution.previewImageUrl = url;
                    this.solution.previewImageUrlName = fileName;
                })
                .catch((error) => {
                    if (error instanceof FileByteSizeLimitError) {
                        this.$helpers.showFailNotification(
                            'File size exceeds the maximum amount of 2Mbs',
                        );
                    } else if (error instanceof FileTypeError) {
                        const [head, ...tail] = this.fileConfig.fileSelectorAllowedTypes;
                        this.$helpers.showFailNotification(
                            `File needs to be of type ${tail.join(', ')} or ${head}`,
                        );
                    } else {
                        this.$helpers.showFailNotification(
                            'An error occurred while uploading your image. Please check your format and try again.',
                        );
                    }
                })
                .finally(() => {
                    this.isImageLoading = false;
                });
        },

        handleCollateralSelected({ previewImageUrl }) {
            if (this.context === 'product') {
                this.solution.previewImageUrl = previewImageUrl;
            }
        },

        handleLibrarySelect(event) {
            if (this.context === 'video') {
                this.solution.videoUrl = event.url;
            }
        },

        handleIconSelected(event) {
            if (this.context === 'product') {
                this.isImageLoading = true;
                return new Promise((resolve, reject) => {
                    this.mediaRequest(event)
                        .then(({ url }) => {
                            this.solution.previewImageUrl = url;
                            this.solution.isPreviewImageSvg = true;
                            resolve();
                        })
                        .catch(() => {
                            this.$helpers.showFailNotification(
                                'An error occurred while uploading your icon.',
                            );
                            reject();
                        })
                        .finally(() => {
                            this.isImageLoading = false;
                        });
                });
            }
        },

        handleVideoUpload(e) {
            this.isVideoLoading = true;
            return new Promise((resolve, reject) => {
                this.getBase64Url(e, this.videoFileConfig)
                    .then(({ encodingStr, name }) => {
                        const videoRequest = this.mediaRequest({ name, url: encodingStr });
                        const videoThumbnailRequest = this.$helpers
                            .createBase64VideoThumbnail(name, encodingStr)
                            .then(({ sourceName, sourceUrl }) => {
                                return this.mediaRequest({
                                    name: sourceName,
                                    url: sourceUrl,
                                });
                            });

                        return Promise.all([videoRequest, videoThumbnailRequest]);
                    })
                    .then((values) => {
                        this.solution.videoUrl = values[0].url;
                        this.videoSrcName = values[0].fileName;
                        this.solution.videoThumbnail = values[1].url;
                    })
                    .then(resolve)
                    .catch((error) => {
                        if (error instanceof FileByteSizeLimitError) {
                            this.$helpers.showFailNotification(
                                'File size exceeds the maximum amount of 4Mbs',
                            );
                        } else if (error instanceof FileTypeError) {
                            const [head, ...tail] = this.fileConfig.fileSelectorAllowedTypes;
                            this.$helpers.showFailNotification(
                                `File needs to be of type ${tail.join(', ')} or ${head}`,
                            );
                        } else {
                            this.$helpers.showFailNotification(
                                'An error occurred while uploading your video. Please check your format and try again.',
                            );
                        }
                        reject();
                    })
                    .finally(() => {
                        this.isVideoLoading = false;
                    });
            });
        },

        handleDelete() {
            this.solution.videoUrl = null;
            this.solution.videoThumbnail = null;
        },

        mediaRequest(element) {
            return new Promise((resolve, reject) => {
                const media = {
                    name: element.name,
                    url: element.url,
                };
                FileService.post(media)
                    .then(({ data }) => {
                        resolve({ fileName: element.name, url: data });
                    })
                    .catch(() => {
                        this.$helpers.showFailNotification(`Error trying to save ${element.name}`);
                        reject();
                    });
            });
        },

        setContext(value) {
            this.context = value;
        },
    },
};
</script>

<style>
.inline-solution-widget .solution-widget-container {
    border: 1px solid #cac6cb;
    position: relative;
    width: 100%;
    height: 100%;
    border-radius: 16px;
    padding: 24px;
    margin-bottom: 32px;
}

.inline-solution-widget .solution-widget-container:first-child {
    margin-top: 12px;
}

.inline-solution-widget .solution-widget-container::before {
    content: '';
    position: absolute;
    top: -1px;
    height: 1px;
    background: #fff;
}

.inline-solution-widget .solution-widget-container::before {
    right: 0;
    left: 0;
    margin: 0 auto;
    width: 36%;
}

.inline-solution-widget .solution-widget-container span.solution-widget-header {
    position: absolute;
    right: 0;
    left: 0;
    top: 0;
    margin: 0 auto;
    transform: translateY(-50%);
    text-align: center;
    font: normal normal 500 18px/11px 'Roboto';
    letter-spacing: 0px;
    color: #382153;
}

.inline-solution-widget.widget .widget-contents-container .content-wrapper {
    padding: 16px 24px;
}

.inline-solution-widget .widget-input-container:not(:last-child) {
    margin-bottom: 16px;
}

.inline-solution-widget .widget-input-container .loading-screen {
    margin: auto;
    text-align: left;
}

.inline-solution-widget .widget-input-container label {
    display: block;
    font: 500 16px/1em 'Roboto';
    margin-bottom: 10px;
    color: #382153;
}

.inline-solution-widget.widget .fr-toolbar.fr-top {
    background: linear-gradient(90deg, rgba(80, 82, 254, 1) 0%, rgba(75, 201, 254, 1) 100%);
}

.inline-solution-widget .widget-input-container .app-drag-and-drop-area {
    display: inline-block;
    margin-top: 16px;
    width: 100%;
}

.inline-solution-widget .widget-input-container .app-drag-and-drop-area .multiple-image-preview {
    display: none;
}

.inline-solution-widget .trash-can {
    width: 26px;
    height: 26px;
    border-radius: 50%;
    background: #ee206f 0% 0% no-repeat padding-box;
    position: absolute;
    display: flex;
    top: -12px;
    right: -12px;
    cursor: pointer;
    z-index: 99;
}

.inline-solution-widget .trash-can .trash-can-svg {
    width: 14px;
    height: 18px;
    margin: auto;
}

.inline-solution-widget .trash-can .trash-can-svg path,
.inline-solution-widget .trash-can .trash-can-svg rect {
    fill: #fff;
}

.inline-solution-widget .preview-image-solutions {
    display: inline-block;
    margin-top: 16px;
    position: relative;
    width: 152px;
    height: 144px;
}

.inline-solution-widget .preview-image-solutions img {
    border: 1px solid #cac6cb;
    border-radius: 16px;
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.inline-solution-widget .preview-video-solutions {
    display: inline-block;
    margin-top: 16px;
    position: relative;
}

.inline-solution-widget .preview-video-solutions .ratio-box {
    width: 256px;
    height: 144px;
    position: relative;
    padding-bottom: 56.25%;
}

.inline-solution-widget .preview-video-solutions iframe {
    border-radius: 12px;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
}

.inline-solution-widget .preview-video-solutions video {
    border-radius: 12px;
    width: 256px;
    height: 144px;
}

.inline-solution-widget .widget-contents-container .transparent-btn {
    background: transparent;
    color: #877d88;
    border: 1px solid #cac6cb;
}

.inline-solution-widget .widget-contents-container .transparent-btn:hover {
    background: #e1e0e5;
}
</style>
